import { useState, useEffect } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  GoogleAuthProvider,
  signOut,
} from "firebase/auth";
import { auth } from "../firebase";

const googleProvider = new GoogleAuthProvider();
// Request the user's email so it's available after Google sign-in
googleProvider.addScope("email");
googleProvider.addScope("profile");

export function useAuth() {
  const [user, setUser]       = useState(undefined); // undefined = still loading
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsub;
    try {
      unsub = onAuthStateChanged(
        auth,
        (u) => { setUser(u ?? null); setLoading(false); },
        (err) => {
          // eslint-disable-next-line no-console
          console.error("[useAuth] onAuthStateChanged error:", err);
          setUser(null);
          setLoading(false);
        }
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[useAuth] Failed to subscribe to auth state:", err);
      setUser(null);
      setLoading(false);
    }
    return () => { if (unsub) unsub(); };
  }, []);

  const loginWithEmail  = (email, password) =>
    signInWithEmailAndPassword(auth, email, password);

  const signupWithEmail = (email, password) =>
    createUserWithEmailAndPassword(auth, email, password);

  const loginWithGoogle = () =>
    signInWithPopup(auth, googleProvider);

  // Phone sign-in (same Firebase phone auth the mobile app uses, so the same
  // number resolves to the same uid and the same users/{uid} document).
  // `phoneE164` must look like "+919876543210". Returns a ConfirmationResult;
  // call .confirm(code) on it to finish signing in.
  const sendPhoneOtp = async (phoneE164, containerId) => {
    clearPhoneVerifier();
    window.__jbRecaptcha = new RecaptchaVerifier(auth, containerId, { size: "invisible" });
    try {
      return await signInWithPhoneNumber(auth, phoneE164, window.__jbRecaptcha);
    } catch (err) {
      clearPhoneVerifier();
      throw err;
    }
  };

  const logout = () => signOut(auth);

  return {
    user, loading,
    loginWithEmail, signupWithEmail, loginWithGoogle,
    sendPhoneOtp, clearPhoneVerifier,
    logout,
  };
}

export function clearPhoneVerifier() {
  if (window.__jbRecaptcha) {
    try { window.__jbRecaptcha.clear(); } catch { /* already cleared */ }
    window.__jbRecaptcha = null;
  }
}
